import { 
  selectElement,
  valueOfXNode,
} from "./domHelper";
// 내용이 많아서 domHelper에서 독립함

/**
 * 엘리먼트의 어트리뷰트의 변화를 체크하여 값의 변화에 반응하도록 함
 * 
 * @param {HTMLElement|string} selector - 요소를 선택하는 CSS 선택자
 *   - HTMLElement: 해당 요소를 직접 사용 이 경우 콜백의 필드 값은 target으로 설정 됨
 *   - string: document.querySelector를 사용하여 요소 선택 이 경우 콜백의 필드 값은 target으로 설정 됨
 * @param {Function|Object} callbackOrObject - 이벤트 핸들러 함수를 포함한 객체 또는 함수
 * 
 * @returns {Object} 다음 속성과 메서드를 포함하는 객체:
 *   - element: 감시 중인 요소
 *   - attributes: Object로 변환된 attribute 값
 *   - setAttribute: element.setAttribute와 같습니다. callbackOrObject 콜백이나 attribute 콜백이 실행될 때는 callback 이벤트를 일으키지 않습니다.
 *   - getAttribute: element.getAttribute 같습니다. callbackOrObject 콜백이나 attribute 콜백이 실행될 때는 callback 이벤트를 일으키지 않습니다.
 *   - removeAttribute: element.removeAttribute 같습니다. callbackOrObject 콜백이나 attribute 콜백이 실행될 때는 callback 이벤트를 일으키지 않습니다.
 *   - dataset : element의 dataset 객체 자체입니다. callbackOrObject 콜백이나 attribute 콜백이 실행될 때는 callback 이벤트를 일으키지 않습니다.
 *   - destroy(): 모든 이벤트 리스너를 제거하고 리소스를 해제하는 메서드
 * 
 * @example
 * const observer = useAttributesObserver("#search-input", (current) => {
 *   current.attributes
 *   return {
 *     // 초기화 되자 마자 watch 트리깅이 필요한 경우
 *     init(newAttributes){ 
 *       this.watch(newAttributes) 
 *     },
 *     watch: (newAttributes, oldAttributes) => {
 *       
 *     },
 *   };
 * });
 * 
 * observer.destry();
 */
export function useAttributesObserver(selector, callbackOrObject) {
  // 요소 선택 처리
  const element = typeof selector === 'string' 
    ? document.querySelector(selector) 
    : selector;
  
  if (!element) {
    throw new Error(`useAttributesObserver: 요소를 찾을 수 없습니다 => ${selector}`);
  }
  
  // 속성 값을 객체로 변환하는 함수
  function getAttributesAsObject(el) {
    const result = {};
    const attributes = el.attributes;
    
    for (let i = 0; i < attributes.length; i++) {
      const attr = attributes[i];
      result[attr.name] = attr.value;
    }
    
    return result;
  }
  
  // 현재 속성 상태 저장
  let currentAttributes = getAttributesAsObject(element);
  let isConfigCallbackRunning = false; // 콜백 실행 중 상태 추적
  
  // 반환 객체 생성
  const returnObj = {
    element,
    get attributes() {
      return getAttributesAsObject(element);
    },
    setAttribute(name, value) {
      isConfigCallbackRunning = true;
      element.setAttribute(name, value);
      isConfigCallbackRunning = false;
    },
    getAttribute(name) {
      return element.getAttribute(name);
    },
    removeAttribute(name) {
      isConfigCallbackRunning = true;
      element.removeAttribute(name);
      isConfigCallbackRunning = false;
    },
    get dataset() {
      return element.dataset;
    },
    destroy() {
      // MutationObserver 해제
      if (observer) {
        observer.disconnect();
      }
    }
  };
  
  // 콜백 또는 객체로부터 핸들러 설정
  let handlers = null;
  if (typeof callbackOrObject === "function") {
    handlers = callbackOrObject(returnObj);
  } else if (typeof callbackOrObject === "object") {
    handlers = callbackOrObject;
  } else {
    throw new Error("useAttributesObserver: 두 번째 매개변수는 함수 또는 객체여야 합니다");
  }
  
  // 속성 변경 감지 및 처리 함수
  function handleAttributeChange() {
    // 콜백 실행 중이면 무시 (무한 루프 방지)
    if (isConfigCallbackRunning) return;
    
    const newAttributes = getAttributesAsObject(element);
    
    // 변경 사항이 있는지 확인
    if (typeof handlers.watch === 'function') {
      isConfigCallbackRunning = true;
      handlers.watch(newAttributes, currentAttributes);
      isConfigCallbackRunning = false;
    }
    
    // 현재 속성 상태 업데이트
    currentAttributes = newAttributes;
  }
  
  // MutationObserver 설정
  const observer = new MutationObserver((mutations) => {
    let hasAttributeChange = false;
    
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        hasAttributeChange = true;
        break;
      }
    }
    
    if (hasAttributeChange) {
      handleAttributeChange();
    }
  });
  
  // 속성 변경 감시 시작
  observer.observe(element, {
    attributes: true,
    attributeOldValue: true
  });
  
  // 초기 속성 상태 설정
  currentAttributes = getAttributesAsObject(element);
  if(handlers.init){
    const {init, ...otherHandlers} = handlers;
    init.bind(otherHandlers)(currentAttributes);
  }
  return returnObj;
}


/**
 * 컴포지션 상태를 체크하여 필터형 검색어 입력 중 데이터가 비지 않도록 도와주는 함수
 * 입력 필드의 컴포지션 이벤트를 감지하여 IME 입력 중과 완료 시 다른 처리를 할 수 있게 합니다.
 * 내부적으로 값을 평가할때는 모두 trim이 됨 그럼으로 그래서 문자의 끝의 space나 개행은 input을 일으킬 수 없음
 * (이전 입력과 같은 값은 이벤트를 일으키지 않음)
 * 
 * @param {HTMLElement|string|Object} selector - 검색 입력 요소 또는 요소를 선택하는 CSS 선택자
 *   - HTMLElement: 해당 요소를 직접 사용 이 경우 콜백의 필드 값은 target으로 설정 됨
 *   - string: document.querySelector를 사용하여 요소 선택 이 경우 콜백의 필드 값은 target으로 설정 됨
 *   - Object: 여러 입력 필드를 키와 선택자로 지정 ({name: "#name", phone: "#phone"})
 * @param {Function|Object} callbackOrObject - 이벤트 핸들러 함수를 포함한 객체 또는 함수
 * 
 * @returns {Object} 다음 속성과 메서드를 포함하는 객체:
 *   - elements: 감시 중인 모든 입력 요소
 *   - values: 현재 모든 입력 요소의 값
 *   - update(): 입력 값을 수동으로 업데이트하는 메서드
 *   - destroy(): 모든 이벤트 리스너를 제거하고 리소스를 해제하는 메서드
 * 
 * @example
 * const searchComp = useInputComposition("#search-input", (current) => {
 *   return {
 *     input: (inputs) => {
 *       // 컴포지션 중일 때 실행되는 코드
 *       const results = data.filter(item => item.name.includes(inputs.target));
 *       if (results.length > 0) {
 *         renderResults(results);
 *       }
 *     },
 *     commit (inputs){
 *       // 입력 중 enter나 포커스아웃 할 때 실행되는 코드
 *       renderResults(
 *         data.filter(item => item.name.includes(inputs.target))
 *       )
 *     }
 *   };
 * });
 */

export function useInputComposition(selector, callbackOrObject) {
  // 입력 필드 구성 처리
  const elements = {};
  const values = {}; // 현재 값 (trim 하지 않은)
  const trimmedValues = {}; // trim된 값 (비교용)
  const compositioning = {}; // 컴포지션 상태 추적
  const lastFiredValues = {}; // 마지막으로 발생시킨 이벤트 값 추적
  const confirmedValues = {}; // 확정된 값 추적 (commit 이벤트용)
  
  // 단일 요소나 문자열 선택자를 객체 형태로 변환
  let selectorMap = {};
  if (typeof selector === 'string' || selector instanceof HTMLElement) {
    selectorMap = { target: selector };
  } else if (typeof selector === 'object' && selector !== null) {
    selectorMap = selector;
  } else {
    throw new Error("useInputComposition: 첫 번째 매개변수는 문자열, HTML 요소 또는 객체여야 합니다");
  }
  
  // 각 선택자에 대한 요소 찾기 및 초기 값 설정
  for (const key in selectorMap) {
    if (Object.prototype.hasOwnProperty.call(selectorMap, key)) {
      const el = selectElement(selectorMap[key]);
      if (!el) {
        throw new Error(`useInputComposition: 요소를 찾을 수 없습니다 => ${selectorMap[key]}`);
      }
      
      elements[key] = el;
      const initialValue = valueOfXNode(el);
      values[key] = initialValue;
      trimmedValues[key] = initialValue.trim();
      compositioning[key] = false;
      lastFiredValues[key] = initialValue.trim();
      confirmedValues[key] = initialValue.trim();
    }
  }
  
  // 반환 객체 생성
  const returnObj = {
    get elements() {
      return { ...elements };
    },
    get values() {
      // 현재 값 동적 반환
      const currentValues = {};
      for (const key in elements) {
        currentValues[key] = valueOfXNode(elements[key]);
      }
      return currentValues;
    },
    update() {
      // 모든 입력 필드에 대해 수동 업데이트 수행
      for (const key in elements) {
        const currentValue = valueOfXNode(elements[key]);
        updateValue(key, currentValue);
        triggerInputComposition(key, currentValue);
      }
    },
    destroy() {
      // 모든 입력 필드의 이벤트 리스너 제거
      for (const key in elements) {
        const el = elements[key];
        el.removeEventListener('input', inputHandlers[key]);
        el.removeEventListener('compositionstart', compositionStartHandlers[key]);
        el.removeEventListener('compositionend', compositionEndHandlers[key]);
        el.removeEventListener('blur', blurHandlers[key]);
        el.removeEventListener('keydown', keydownHandlers[key]);
      }
    }
  };
  
  // 콜백 또는 객체로부터 핸들러 설정
  let handlers = null;
  if (typeof callbackOrObject === "function") {
    handlers = callbackOrObject(returnObj);
  } else if (typeof callbackOrObject === "object") {
    handlers = callbackOrObject;
  } else {
    throw new Error("useInputComposition: 두 번째 매개변수는 함수 또는 객체여야 합니다");
  }
  
  // 현재 모든 입력 값을 담은 객체를 반환하는 함수
  function getAllInputValues(useTrimmed = false) {
    const allInputs = {};
    for (const key in elements) {
      allInputs[key] = useTrimmed ? values[key].trim() : values[key];
    }
    return allInputs;
  }
  
  // 값 업데이트 함수
  function updateValue(key, value) {
    values[key] = value;
    trimmedValues[key] = value.trim();
  }
  
  // 이벤트 발생 함수 (입력 중 콜백)
  function triggerInputComposition(key, value) {
    if (typeof handlers.composition === 'function') {
      const trimmedValue = value.trim();
      
      // 이전 발생 값과 다를 때만 이벤트 발생 (중복 방지)
      if (lastFiredValues[key] !== trimmedValue) {
        updateValue(key, value);
        lastFiredValues[key] = trimmedValue;
        
        handlers.composition(getAllInputValues(true));
      }
    }
  }
  
  // 값 확정 이벤트 발생 함수 (입력 완료 콜백)
  function triggerChanged(key, value) {
    if (typeof handlers.commit === 'function') {
      const trimmedValue = value.trim();
      
      // 이전 확정 값과 다를 때만 이벤트 발생
      if (confirmedValues[key] !== trimmedValue) {
        updateValue(key, value);
        confirmedValues[key] = trimmedValue;
        
        handlers.commit(getAllInputValues(true));
      }
    }
  }
  
  // 입력 이벤트 처리 함수
  function handleInput(element, key) {
    const currentValue = valueOfXNode(element);
    updateValue(key, currentValue);
    
    // 상태에 관계없이 값이 변경되면 이벤트 발생
    triggerInputComposition(key, currentValue);
  }
  
  // 컴포지션 시작 이벤트 처리
  function handleCompositionStart(element, key) {
    compositioning[key] = true;
  }
  
  // 컴포지션 종료 이벤트 처리
  function handleCompositionEnd(element, key) {
    compositioning[key] = false;
    const currentValue = valueOfXNode(element);
    
    // 컴포지션 종료 시 현재 값으로 이벤트 발생
    triggerInputComposition(key, currentValue);
  }
  
  // blur 이벤트 처리 함수
  function handleBlur(element, key) {
    // 컴포지션 상태 종료
    compositioning[key] = false;
    const currentValue = valueOfXNode(element);
    
    // 포커스 아웃 시 confirm 처리 (단 이전과 동일한 값이면 무시)
    triggerChanged(key, currentValue);
  }
  
  // 키다운 이벤트 처리 함수 (Enter 키 처리)
  function handleKeydown(event, key) {
    // Enter 키 입력 시 값 확정 처리
    if (event.key === 'Enter') {
      const currentValue = valueOfXNode(event.target);
      triggerChanged(key, currentValue);
    }
  }
  
  // 각 요소별 이벤트 핸들러 생성
  const inputHandlers = {};
  const compositionStartHandlers = {};
  const compositionEndHandlers = {};
  const blurHandlers = {};
  const keydownHandlers = {};
  
  for (const key in elements) {
    // 입력 이벤트 핸들러
    inputHandlers[key] = function(event) {
      handleInput(event.target, key);
    };
    
    // 컴포지션 시작 이벤트 핸들러
    compositionStartHandlers[key] = function(event) {
      handleCompositionStart(event.target, key);
    };
    
    // 컴포지션 종료 이벤트 핸들러
    compositionEndHandlers[key] = function(event) {
      handleCompositionEnd(event.target, key);
    };
    
    // 포커스 아웃 이벤트 핸들러
    blurHandlers[key] = function(event) {
      handleBlur(event.target, key);
    };
    
    // 키다운 이벤트 핸들러 (Enter 키 감지)
    keydownHandlers[key] = function(event) {
      handleKeydown(event, key);
    };
    
    // 이벤트 리스너 등록
    const el = elements[key];
    el.addEventListener('input', inputHandlers[key]);
    el.addEventListener('compositionstart', compositionStartHandlers[key]);
    el.addEventListener('compositionend', compositionEndHandlers[key]);
    el.addEventListener('blur', blurHandlers[key]);
    el.addEventListener('keydown', keydownHandlers[key]);
  }
  
  if(handlers.init){
    const {init, ...otherHandlers} = handlers;
    init.bind(otherHandlers)(returnObj.values);
  }

  return returnObj;
}

export function debounce(fn, delay = 400) {
  let timer = null;
  return function(...args) {
    const context = this;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}
